import CheckIcon from '@mui/icons-material/Check';
import { Box, Chip, Skeleton, Tooltip, Typography, useTheme } from '@mui/material';
import { useIntl } from 'react-intl';

import { useAttributeSelection } from './useAttributeSelection';
import { AddedConcept } from '../../state/createProjectSlice';
import type { AttributeResponse } from '../../types/response';

interface AttributeSelectionProps {
  concept: AddedConcept;
}
export const AttributeSelection = ({ concept }: AttributeSelectionProps) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const {
    attributes,
    selectedAttributeIds,
    handleRemoveAttribute,
    handleAddAttribute,
    isAttributesLoading,
    mandatoryAttributeNames,
    isMandatoryAttributeNamesLoading,
  } = useAttributeSelection({ concept });
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
        <Typography variant="body1">
          {formatMessage({
            id: 'createProjectWizard.step.conceptGroupsAndAttributes.selectRequiredAttributes.title',
          })}
        </Typography>
        <Typography variant="body2" sx={{ color: 'error.main' }}>
          *
        </Typography>
      </Box>
      {isAttributesLoading || isMandatoryAttributeNamesLoading ? (
        <Box sx={{ display: 'flex', gap: theme.spacing(3) }}>
          <Skeleton variant="text" width="100px" height="40px" />
          <Skeleton variant="text" width="100px" height="40px" />
          <Skeleton variant="text" width="100px" height="40px" />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: theme.spacing(3) }}>
          {mandatoryAttributeNames.map((attrName: string) => (
            <Tooltip
              key={`tooltip-${attrName}`}
              title={formatMessage(
                {
                  id: 'createProjectWizard.step.conceptGroupsAndAttributes.mandatoryAttribute.chip.tooltip',
                },
                { attributeName: attrName }
              )}
              arrow
            >
              <Chip
                icon={<CheckIcon />}
                key={attrName}
                label={attrName}
                variant="outlined"
                size="small"
                sx={{
                  p: theme.spacing(1),
                  backgroundColor: theme.palette.blue[50],
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  fontSize: '13px',
                  cursor: 'pointer',
                  '& .MuiChip-icon': {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            </Tooltip>
          ))}
          {attributes.map((attribute: AttributeResponse) =>
            selectedAttributeIds.includes(attribute.id) ? (
              <Chip
                onClick={() => handleRemoveAttribute(attribute)}
                icon={<CheckIcon />}
                key={attribute.id}
                label={attribute.label}
                variant="outlined"
                size="small"
                sx={{
                  p: theme.spacing(1),
                  backgroundColor: theme.palette.blue[50],
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  fontSize: '13px',
                  cursor: 'pointer',
                  '& .MuiChip-icon': {
                    color: theme.palette.primary.main,
                  },
                }}
              />
            ) : (
              <Chip
                onClick={() => handleAddAttribute(attribute)}
                key={attribute.id}
                label={attribute.label}
                variant="outlined"
                size="small"
                sx={{
                  p: theme.spacing(1),
                  backgroundColor: theme.palette.grey[200],
                  fontSize: '13px',
                  border: 0,
                  cursor: 'pointer',
                }}
              />
            )
          )}
        </Box>
      )}
    </Box>
  );
};
